import { createContext, useContext, useEffect } from 'react'
import type { CSSProperties } from 'react'
import { BedDouble, KeyRound, Receipt, Tag, Tractor, Truck, type LucideIcon } from 'lucide-react'

/**
 * Vertical separation for the demand/listing side of the app.
 *
 * One context drives two things and nothing else: which discovery surface a
 * consumer/creator lands on, and which `RoleNav` items render. Everything below
 * (auth, profile, notifications, earnings, carts) stays shared. Cross-vertical
 * operators (ops/admin) are NOT scoped by this — they filter, they don't switch.
 * Fulfiller is Moves-only chrome, not a cross-vertical filter role.
 */
export type Vertical = 'farm_to_table' | 'stays' | 'thrifts' | 'moves'

/** Roles whose display name varies by vertical. Staff roles are cross-vertical. */
export type MarketplaceRole = 'consumer' | 'creator' | 'fulfiller'

/**
 * Fulfiller work lives only in Moves (the logistics vertical).
 * Farm-to-Table, Stays and Thrifts no longer expose the fulfiller role in the
 * role switcher or Work nav — those markets use consumer + creator (seller/host) only.
 *
 * UI polish C′: when fulfiller chrome is shown, brand tokens must be Moves indigo
 * (`verticalThemeVars('moves')` / AppShell active vertical), not fixed emerald and
 * not ops-neutral stone. Semantic success still uses fixed emerald (option B).
 */
export function isDrivingVertical(vertical: Vertical): boolean {
  return vertical === 'moves'
}

export type VerticalMeta = {
  id: Vertical
  /** Canonical backend vertical id (e.g. action_log.vertical).
   *
   *  Equal to `id` for every vertical today. The field survives because it did not always
   *  hold: Stays was `homestay` here and `homestay_nagaland` in the backend until the two
   *  were collapsed into `stays` (backend migration 0057), and one vertical answering to
   *  two strings is precisely how the UI and the data drift apart. Keep reading actions and
   *  API calls off this field rather than off `id`, so a future divergence has one place to
   *  live. */
  backendId: string
  /** Consumer-facing brand name (intent-based, never the vertical id). */
  label: string
  tagline: string
  /**
   * What each marketplace role is *called* here.
   *
   * The three roles are deliberately shared across verticals — a thrift seller
   * and a Farm-to-Table creator have identical permissions, so splitting them would
   * duplicate the authorization model to change a word, push vertical names into
   * core participant data (Rule 8), and hand a user six roles to switch between.
   * Only the vocabulary is vertical-specific. New *permissions* still warrant a
   * new role; new *wording* does not.
   */
  roleLabels: Record<MarketplaceRole, string>
  /**
   * Per-vertical icon for a marketplace role, where the shared one in `roleConfig`
   * is wrong here. Overrides only: consumer and fulfiller carry generic marks (a
   * basket, a truck) that read correctly everywhere, so they are omitted and fall
   * back. Creator does not — its shared mark is a seedling, which named a thrift
   * "Seller" and a homestay "Host" with a farming glyph.
   */
  roleIcons: Partial<Record<MarketplaceRole, LucideIcon>>
  /** Brand mark for the vertical: the launcher tile, and the app header on
   *  both signed-in (`AppShell`) and signed-out (`PublicHeader`) surfaces. */
  icon: LucideIcon
  /** Where the launcher tile drops the user. */
  home: string
  /** Tailwind accent classes for the tile. */
  accent: string
  /** Full brand color scale (shade → hex) driving the `brand-*` CSS-var token
   *  for demand-side chrome/buttons. Mirrors a Tailwind palette (50–950). */
  theme: Record<string, string>
  /** Live verticals are enterable; others render as "coming soon". */
  status: 'live' | 'coming_soon'
}

export const VERTICALS: VerticalMeta[] = [
  {
    id: 'farm_to_table',
    backendId: 'farm_to_table',
    label: 'Farm-to-Table',
    tagline: 'Fresh produce from local farms',
    // Fulfiller is not offered here — driving is Moves-only.
    roleLabels: { consumer: 'Consumer', creator: 'Creator', fulfiller: 'Fulfiller' },
    // The shared seedling is the Farm-to-Table mark to begin with — nothing to override.
    roleIcons: {},
    icon: Tractor,
    home: '/marketplace',
    accent: 'text-emerald-700 bg-emerald-50',
    theme: {
      '50': '#ecfdf5',
      '100': '#d1fae5',
      '200': '#a7f3d0',
      '300': '#6ee7b7',
      '400': '#34d399',
      '500': '#10b981',
      '600': '#059669',
      '700': '#047857',
      '800': '#065f46',
      '900': '#064e3b',
      '950': '#022c22',
    },
    status: 'live',
  },
  {
    id: 'stays',
    backendId: 'stays',
    label: 'Stays',
    tagline: 'Homestays, campsites and cabins',
    // Fulfiller role is not offered here — only under Moves.
    roleLabels: { consumer: 'Guest', creator: 'Host', fulfiller: 'Fulfiller' },
    // Keys, for the person handing them over. Not BedDouble — that is the Stays
    // brand mark in the same header, one role chip away.
    roleIcons: { creator: KeyRound },
    icon: BedDouble,
    home: '/homestays',
    accent: 'text-amber-700 bg-amber-50',
    theme: {
      '50': '#fffbeb',
      '100': '#fef3c7',
      '200': '#fde68a',
      '300': '#fcd34d',
      '400': '#fbbf24',
      '500': '#f59e0b',
      '600': '#d97706',
      '700': '#b45309',
      '800': '#92400e',
      '900': '#78350f',
      '950': '#451a03',
    },
    status: 'live',
  },
  {
    id: 'thrifts',
    backendId: 'thrifts',
    label: 'Thrifts',
    tagline: 'Give good things a second life',
    // Fulfiller role is not offered here — thrift hub jobs open under Moves Work.
    roleLabels: { consumer: 'Buyer', creator: 'Seller', fulfiller: 'Fulfiller' },
    // A receipt: the seller is the side that gets paid, and the slip reads at
    // 14px where two overlapping coins would merge. Not Tag/Tags — the singular
    // Tag is the Thrifts brand mark sitting in the same header.
    roleIcons: { creator: Receipt },
    icon: Tag,
    home: '/thrifts',
    accent: 'text-sky-700 bg-sky-50',
    theme: {
      '50': '#f0f9ff',
      '100': '#e0f2fe',
      '200': '#bae6fd',
      '300': '#7dd3fc',
      '400': '#38bdf8',
      '500': '#0ea5e9',
      '600': '#0284c7',
      '700': '#0369a1',
      '800': '#075985',
      '900': '#0c4a6e',
      '950': '#082f49',
    },
    status: 'live',
  },
  {
    id: 'moves',
    backendId: 'moves',
    label: 'Moves',
    tagline: 'Book a vehicle to move your load',
    // Customer books; supply is Fulfiller. Creator is unused for Moves listings.
    roleLabels: { consumer: 'Customer', creator: 'Fulfiller', fulfiller: 'Fulfiller' },
    roleIcons: { fulfiller: Truck },
    icon: Truck,
    home: '/moves',
    accent: 'text-indigo-700 bg-indigo-50',
    theme: {
      '50': '#eef2ff',
      '100': '#e0e7ff',
      '200': '#c7d2fe',
      '300': '#a5b4fc',
      '400': '#818cf8',
      '500': '#6366f1',
      '600': '#4f46e5',
      '700': '#4338ca',
      '800': '#3730a3',
      '900': '#312e81',
      '950': '#1e1b4b',
    },
    status: 'live',
  },
]

export const DEFAULT_VERTICAL: Vertical = 'farm_to_table'
export const VERTICAL_STORAGE_KEY = 'osle.activeVertical'

export function getVerticalMeta(id: Vertical): VerticalMeta {
  return VERTICALS.find((v) => v.id === id) ?? VERTICALS[0]
}

function brandVarsFromScale(theme: Record<string, string>): CSSProperties {
  const vars: Record<string, string> = {}
  // Override the exact Tailwind token vars the `brand-*` utilities read, so the
  // override inherits into the subtree (single indirection — reliable).
  for (const [shade, hex] of Object.entries(theme)) {
    vars[`--color-brand-${shade}`] = hex
  }
  return vars as CSSProperties
}

/** Inline style setting the `--brand-*` CSS vars for a vertical's chrome.
 *  Apply on a wrapper around demand-side surfaces (consumer/creator). Farm-to-Table's
 *  palette equals the :root default, so applying it is a harmless no-op. */
export function verticalThemeVars(id: Vertical): CSSProperties {
  return brandVarsFromScale(getVerticalMeta(id).theme)
}

/**
 * Platform (auth / account) brand scale — always emerald, never the sticky vertical.
 *
 * Auth UI (login, register, reset, onboarding) is account-level, not a vertical.
 * When the Auth modal opens over Thrifts/Stays/Moves, or onboarding sits next to a
 * themed shell, `brand-*` would otherwise inherit sky/amber/indigo. Pin emerald so
 * primary CTAs and focus rings stay platform-green (UI polish option A).
 *
 * Farm-to-Table's palette is the same hexes as `:root`; using it here is intentional
 * so consumer-facing platform and FTT stay one green family. Ops/admin use
 * {@link consoleThemeVars} instead — soft pink accent (`#fa3a92`), not produce green.
 */
export function platformBrandVars(): CSSProperties {
  return verticalThemeVars('farm_to_table')
}

/**
 * Ops/admin console brand scale — soft magenta-pink, not Farm-to-Table emerald.
 *
 * Without this, "neutral" ops chrome was just the default emerald tokens, so staff
 * screens looked like FTT. Console is tooling across every vertical; it must not
 * share a marketplace colour (UI polish E). Semantic success still uses fixed
 * `emerald-*` (option B).
 *
 * Mid accent `#fa3a92` sampled from a soft live-pill pink (hot pink / magenta, not
 * deep rose-red). Scale is pink-tinted so primary buttons stay lively rather than
 * blood-red.
 */
export const CONSOLE_THEME: Record<string, string> = {
  '50': '#fdf2f8',
  '100': '#fce7f3',
  '200': '#fbcfe8',
  '300': '#f9a8d4',
  '400': '#fa3a92',
  '500': '#ec4899',
  '600': '#db2777',
  '700': '#c0266b',
  '800': '#be185d',
  '900': '#a91d5c',
  // Shared by active nav and primary buttons/links (bg-brand-950 / text-brand-*).
  '950': '#be185d',
}

export function consoleThemeVars(): CSSProperties {
  return brandVarsFromScale(CONSOLE_THEME)
}

/** Staff roles that use console chrome rather than a marketplace vertical brand. */
export function isConsoleRole(role: string | null | undefined): boolean {
  return role === 'ops' || role === 'admin'
}

export type VerticalContextValue = {
  activeVertical: Vertical
  setVertical: (v: Vertical) => void
}

/**
 * Default value lets consumers (and tests) read the context without a provider.
 * It resolves to the Farm-to-Table nav/discovery so existing behavior is preserved.
 */
export const VerticalContext = createContext<VerticalContextValue>({
  activeVertical: DEFAULT_VERTICAL,
  setVertical: () => {},
})

/**
 * Display name for a role in a vertical.
 *
 * Staff roles (ops/admin) are cross-vertical — they filter rather than enter a
 * vertical — so they keep their fixed name and fall through unchanged.
 */
export function getRoleLabel(vertical: Vertical, role: string, fallback: string): string {
  const labels = getVerticalMeta(vertical).roleLabels
  return labels[role as MarketplaceRole] ?? fallback
}

/**
 * Icon for a role in a vertical — the companion to {@link getRoleLabel}, and it
 * exists because the two had drifted: the label said "Seller" while the icon
 * stayed the Farm-to-Table seedling. Anywhere a role is *named* per vertical it
 * must be *drawn* per vertical too. Falls back to the shared `roleConfig` icon.
 */
export function getRoleIcon(vertical: Vertical, role: string, fallback: LucideIcon): LucideIcon {
  const icons = getVerticalMeta(vertical).roleIcons
  return icons[role as MarketplaceRole] ?? fallback
}

export function useVertical(): VerticalContextValue {
  return useContext(VerticalContext)
}

/**
 * Declare which vertical a surface belongs to, and make the context agree.
 *
 * The active vertical is sticky, so a page that only *reads* it renders whatever
 * the last visited vertical was. Reaching `/marketplace` by deep link or browser
 * back after browsing Stays used to leave the header reading "OSLE Stays". That
 * was cosmetic while the brand was decoration; now that it is the link to the
 * vertical's home, a stale value points the user at the wrong vertical.
 *
 * Every surface pinned to one vertical should call this. Surfaces that are
 * genuinely cross-vertical (the launcher, operator consoles) must not.
 */
export function useAssertVertical(vertical: Vertical): void {
  const { activeVertical, setVertical } = useVertical()
  useEffect(() => {
    if (activeVertical !== vertical) setVertical(vertical)
  }, [activeVertical, vertical, setVertical])
}
